// Types for the three-tier source provenance funnel (Searched → Read → Cited).
//
// These mirror the `AssistantResponseSources` payload returned by the backend
// `POST /messages/provenance` endpoint (#20423, source-provenance spec Story 4).

import type { ChunkData, ChunkMetadata } from "./citations";

/**
 * How a source is classified relative to the user's data surfaces. Used to
 * render a provenance chip on Read and Cited items.
 */
export type SourceClassification =
  | "project_file"
  | "org_dataset"
  | "external_library"
  | "adhoc_upload";

/**
 * One retrieval call that surfaced a source (#20423 — RetrievalEvent seam). A
 * chunk may be retrieved by several events (different tools/agents/queries), so
 * each source carries a list of these (earliest by `seq` first). `tool_name` is
 * the internal discriminator and must NEVER be rendered to the user.
 */
export interface ProvenanceRetrievalRef {
  event_id: string;
  /** Internal only — do NOT display to the user. */
  tool_name?: string;
  agent?: string | null;
  query_text?: string | null;
  timestamp?: string | null;
}

/** A source data record as returned in the provenance payload. */
export interface ProvenanceSourceDataRecord {
  id: number;
  source_uid?: string;
  dataset_fk?: number;
  /**
   * Project-folder file chunks are scoped by a project folder instead of a
   * dataset. Present for project-file sources; lets the Sources panel resolve a
   * source into the project file viewer (spec §5.10, same field used by
   * rehypeWrapCite).
   */
  project_folder_fk?: number;
  data: Record<string, any>;
}

/** Minimal dataset descriptor on a provenance item. */
export interface ProvenanceDataset {
  id: number;
  name: string;
  dataset_type?: { id?: number; name?: string } | null;
}

/** A chunk descriptor on a provenance item. */
export interface ProvenanceDataChunk {
  id: number;
  source_data_record_fk?: number;
  chunk_data: ChunkData;
  chunk_metadata: ChunkMetadata;
}

/**
 * A single document-backed source. Shared shape for the Cited tier
 * (`assistant_response_sources`, cited === true) and the Read tier
 * (`read_sources`, cited === false). The shape is intentionally compatible
 * with the legacy `SearchResult` used by {@link SourcesChunkView} so the same
 * chunk-card renderer can be reused.
 */
export interface ProvenanceSource {
  source_data_record: ProvenanceSourceDataRecord;
  dataset: ProvenanceDataset;
  data_chunk: ProvenanceDataChunk;
  classification: SourceClassification;
  retrieved_by_agent: string | null;
  cited: boolean;
  /** Many-to-many retrieval attribution (earliest by seq first). */
  retrieval_events?: ProvenanceRetrievalRef[];
  /** Earliest retrieval event, for compact display. */
  primary_retrieval?: ProvenanceRetrievalRef | null;
  /** = primary_retrieval.timestamp (ISO 8601). */
  retrieved_at?: string | null;
  /** = primary_retrieval.query_text. */
  retrieval_query?: string | null;
  /** Document revision/version, surfaced-when-available (else null). */
  revision?: string | null;
}

/**
 * Provider discriminator for an external (non-chunk) source. Maps to a
 * human-facing provider label in the Sources panel (nrc_adams → "NRC ADAMS",
 * osti → "DOE OSTI", web → "Web").
 */
export type ExternalSourceType = "nrc_adams" | "osti" | "web" | "vnv";

/**
 * An external (ADAMS / OSTI / web / VnV) source surfaced in the response
 * (contract §External sources, schema 1.1.0). `url` is pre-sanitized by the
 * backend (http/https only, else null) and must be rendered as a link element
 * — never raw HTML.
 */
export interface ProvenanceExternalSource {
  source_type: ExternalSourceType;
  title?: string | null;
  /** Family id: ADAMS accession number / OSTI id / canonical URL. */
  identifier?: string | null;
  url?: string | null;
  snippet?: string | null;
  published_date?: string | null;
  classification?: SourceClassification | null;
  retrieved_by_agent?: string | null;
  cited?: boolean | null;
  /** Read tier (#20423 web-read): a non-cited external whose content the model
   * consumed (approved web results, ADAMS/OSTI tool results). Renders in the
   * Read tier alongside internal read chunks. Mutually exclusive with `cited`. */
  read?: boolean | null;
  /** When the owning retrieval event ran (ISO 8601). */
  retrieved_at?: string | null;
  /** Synthetic id the model cites this external by (#20423 A2), and its
   * dialogue-global 1-based number when cited. Present only for cited externals. */
  cite_id?: number | null;
  citation_index?: number | null;
}

/**
 * A cited/read chunk the requester can't resolve (deleted or unauthorized —
 * deliberately indistinguishable, contract §document_unavailable). Only safe
 * metadata is kept: the citation index stays so inline `[N]` numbering never
 * shifts; title/dataset/page/revision are withheld.
 */
export interface ProvenanceUnavailableSource {
  chunk_id: number;
  cited: boolean;
  /** 1-based position in the cited order; null for Read-tier entries. */
  citation_index: number | null;
  document_unavailable: true;
}

/**
 * Human-facing degradation notices (contract §Graceful degradation). Each
 * value is `null` when that situation does not apply; non-null strings are
 * backend copy and must be rendered VERBATIM.
 */
export interface ProvenanceDegradation {
  /** "Generated without consulting any documents" — render alone, no tiers. */
  no_documents: string | null;
  /** Documents present but nothing cited. */
  no_citations: string | null;
  /** Claim-level mapping unavailable; response-level attribution in use. */
  response_level_attribution: string | null;
  /** A sub-agent contributed content without traceable sources. */
  subagent_untraced: string | null;
  /** Display names of the untraced sub-agents (null when none). */
  subagent_untraced_agents: string[] | null;
}

/** Feature flags reported by `GET /datasets/provenance/capabilities`. */
export interface ProvenanceFeatures {
  retrieval_events: boolean;
  claim_level: boolean;
  annotations: boolean;
  revision: boolean;
  external_sources: boolean;
  degradation_v2: boolean;
}

/**
 * Capability-discovery payload (`GET /datasets/provenance/capabilities`).
 * A 404 / fetch error is a normal state meaning "legacy backend" — the FE
 * renders the legacy SourceView, never an error.
 */
export interface ProvenanceCapabilities {
  schema_version: string;
  features: ProvenanceFeatures;
}

/**
 * A Searched-tier entry: one query the agent ran against one or more
 * collections. `tool_name` is internal-only and must never be rendered.
 */
export interface ProvenanceSearchedEntry {
  query_text: string;
  dataset_ids: number[];
  collection_names: string[];
  document_count: number;
  result_count: number;
  agent: string | null;
  /** Internal only — do NOT display to the user. */
  tool_name?: string;
  /** When this query ran (ISO 8601). */
  timestamp?: string;
}

/**
 * A claim-level citation mapping (#20423 §1.3): one span of assistant prose and
 * the chunk_ids cited for it. A claim whose `chunk_ids` names more than one
 * document is a *cross-reference* — those sources support the same claim and are
 * surfaced as "↔ Cross-referenced with [N]" links in the Sources panel + viewer.
 * Produced by the backend `extract_claim_citations`; char offsets index into the
 * (rendered) assistant text.
 */
export interface ProvenanceClaim {
  claim_text: string;
  /**
   * Short topical label for the claim (≤~6 words), generated by the backend for
   * the Cited-tier claim headings (e.g. "EDG start time & testing requirement").
   * Absent until the label-generation task runs; the UI falls back to
   * `claim_text` when unset.
   */
  label?: string;
  chunk_ids: number[];
  char_start?: number;
  char_end?: number;
}

/** Full provenance payload for one assistant response. */
export interface AssistantResponseSources {
  assistant_response_sources: ProvenanceSource[];
  read_sources: ProvenanceSource[];
  external_sources: ProvenanceExternalSource[];
  searched: ProvenanceSearchedEntry[];
  /** Claim→chunk_ids mapping; multi-document claims drive cross-references. */
  claims?: ProvenanceClaim[];
  /**
   * Cited/read chunks the requester can't resolve — kept (with their stable
   * citation_index) so inline `[N]` numbering never shifts (schema 1.1.0).
   */
  unavailable_sources?: ProvenanceUnavailableSource[];
  /** Degradation notices to render verbatim (schema 1.1.0). */
  degradation?: ProvenanceDegradation | null;
  /** Provenance payload schema version (e.g. "1.1.0"). */
  schema_version?: string;
  /**
   * True once the covered turn's end-of-turn persist has landed (message
   * scope: the target message; dialogue scope: the latest assistant turn).
   * The poll stops on `=== true`; absent (legacy backend) keeps the
   * time-bounded polling behavior.
   */
  turn_settled?: boolean | null;
}

export const EMPTY_PROVENANCE: AssistantResponseSources = {
  assistant_response_sources: [],
  read_sources: [],
  external_sources: [],
  searched: [],
  unavailable_sources: [],
};
